/**
 * Grille honoraires U3130 en lecture (BL-0676) — même rendu que la grille compacte U3129 (BL-0672 / BL-0674 / BL-0677) :
 * boîtes lecture seule h-8, largeurs fixes, pied compact.
 */

import { U3130_READONLY_TABLE_CELL_BOX_COMPACT_CLASS } from './u3130ReadonlyTableCellClass';

export { U3130_READONLY_TABLE_CELL_BOX_COMPACT_CLASS };

export const U3130_HONORARIUM_COL_WIDTH_CHECKBOX = '2.5rem';
/** # dossier (~5 chiffres). */
export const U3130_HONORARIUM_COL_WIDTH_PROJECT = '5.5rem';
/** Minimum client ; seule colonne élastique. */
export const U3130_HONORARIUM_COL_WIDTH_CLIENT_MIN = '5rem';
export const U3130_HONORARIUM_COL_WIDTH_NATURES = '12rem';
export const U3130_HONORARIUM_COL_WIDTH_SUBPROJECT = '5rem';
export const U3130_HONORARIUM_COL_WIDTH_HOUR = '6.5rem';

export function u3130HonorariumTableMinWidth(options: { showSubProjectColumn: boolean; dayColumnCount: number }): string {
  const subPart = options.showSubProjectColumn ? ` + ${U3130_HONORARIUM_COL_WIDTH_SUBPROJECT}` : '';
  const hourCols = options.dayColumnCount + 1;
  return `calc(${U3130_HONORARIUM_COL_WIDTH_CHECKBOX} + ${U3130_HONORARIUM_COL_WIDTH_PROJECT} + ${U3130_HONORARIUM_COL_WIDTH_CLIENT_MIN} + ${U3130_HONORARIUM_COL_WIDTH_NATURES}${subPart} + ${hourCols} * ${U3130_HONORARIUM_COL_WIDTH_HOUR})`;
}

export const U3130_HONORARIUM_ROW_CELL_PY = 'py-0.5';
export const U3130_HONORARIUM_CELL_X = 'px-1';

export const U3130_HONORARIUM_PROJECT_COL_CLASS = 'min-w-[5.5rem] w-[5.5rem] max-w-[5.5rem]';
export const U3130_HONORARIUM_CUSTOMER_COL_CLASS = 'min-w-[5rem] w-auto max-w-[8rem]';
export const U3130_HONORARIUM_NATURES_COL_CLASS = 'min-w-[12rem] w-[12rem] max-w-[18rem] tabular-nums';
export const U3130_HONORARIUM_SUBPROJECT_COL_CLASS = 'min-w-[4rem] w-[5rem] max-w-[5rem]';
export const U3130_HONORARIUM_HOUR_COL_CLASS = 'min-w-[6.5rem] w-[6.5rem] shrink-0';

export const U3130_HONORARIUM_CHECKBOX_TD_CLASS = `w-10 px-1 ${U3130_HONORARIUM_ROW_CELL_PY} align-middle [&>div.flex]:box-border [&>div.flex]:min-h-8 [&>div.flex]:w-full [&>div.flex]:items-center [&>div.flex]:justify-center [&>div.flex]:gap-0 [&>div.flex>label:last-child:empty]:hidden`;
export const U3130_HONORARIUM_HOUR_TD_CLASS = `${U3130_HONORARIUM_CELL_X} ${U3130_HONORARIUM_ROW_CELL_PY} ${U3130_HONORARIUM_HOUR_COL_CLASS}`;

/** Ligne tâche : libellé ~2/3 et natures ~1/3 sur les colonnes Projet + Client + Natures. */
export const U3130_HONORARIUM_TASK_ROW_META_COLSPAN = 3;
export const U3130_HONORARIUM_TASK_ROW_SPLIT_WRAPPER_CLASS = 'flex w-full min-w-0 items-stretch gap-1';
export const U3130_HONORARIUM_TASK_ROW_TASK_PART_CLASS =
  'flex min-h-8 min-w-0 basis-2/3 shrink self-stretch whitespace-normal break-words leading-tight';
export const U3130_HONORARIUM_TASK_ROW_NATURE_PART_CLASS =
  'flex min-h-8 min-w-0 basis-1/3 self-stretch tabular-nums text-left whitespace-normal break-all leading-tight';
export const U3130_HONORARIUM_TASK_ROW_BOX_CLASS = `${U3130_READONLY_TABLE_CELL_BOX_COMPACT_CLASS} h-full min-h-8 w-full whitespace-normal leading-tight`;

/**
 * Dossier déplié : la ligne client (parent) et ses lignes tâche (enfants) forment un bloc visuel —
 * même teinte de fond et barre d’accent à gauche ; boîtes blanches dans les enfants pour garder le contraste.
 */
export const U3130_HONORARIUM_EXPANDED_PARENT_ROW_CLASS = 'bg-blue-50 hover:bg-blue-100/70';
export const U3130_HONORARIUM_COLLAPSED_PARENT_ROW_CLASS = 'bg-slate-50/90 hover:bg-slate-100';
/** Ligne client cliquable (déplier / replier) : curseur main, y compris sur les boîtes lecture (`cursor-not-allowed`). */
export const U3130_HONORARIUM_CLICKABLE_PARENT_ROW_CLASS = 'cursor-pointer [&_*]:cursor-pointer!';
export const U3130_HONORARIUM_EXPANDED_CHILD_ROW_CLASS = 'border-b border-blue-100 bg-blue-50/50 hover:bg-blue-50';
/** Barre d’accent sur la première cellule (ombre interne : compatible `border-collapse`). */
export const U3130_HONORARIUM_EXPANDED_ACCENT_CELL_CLASS = 'shadow-[inset_4px_0_0_0_#3b82f6]';
export const U3130_HONORARIUM_EXPANDED_CHILD_BOX_CLASS = 'bg-white! border-blue-100!';

export const U3130_HONORARIUM_HEADER_TH_CLASS = `${U3130_HONORARIUM_CELL_X} py-2.5 text-left font-medium text-gray-700`;
export const U3130_HONORARIUM_DAY_TH_CLASS = `pl-1 pr-[calc(0.25rem+1rem+1px)] py-2.5 text-right tabular-nums font-medium text-gray-700 ${U3130_HONORARIUM_HOUR_COL_CLASS}`;

export const U3130_HONORARIUM_FOOTER_ROW_PY = 'py-[2.5px]';
export const U3130_HONORARIUM_FOOTER_GUTTER_TD_CLASS = `w-10 pl-2 pr-1 ${U3130_HONORARIUM_FOOTER_ROW_PY}`;
export const U3130_HONORARIUM_FOOTER_LABEL_TD_CLASS = `pl-3 pr-3 ${U3130_HONORARIUM_FOOTER_ROW_PY} text-left leading-none`;
export const U3130_HONORARIUM_FOOTER_HOUR_TD_CLASS = `pl-1 ${U3130_HONORARIUM_FOOTER_ROW_PY} align-top text-right text-sm tabular-nums leading-none pr-[calc(0.25rem+0.75rem+1px-4px)] ${U3130_HONORARIUM_HOUR_COL_CLASS}`;
